import React, { useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import i18next from 'i18next';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';

import usePaginationStore from '../../store/usePaginationStore';

const CandidatesTable = ({ data = [], dataCount = 0, dataPages = 1, loading, campaign }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

    const history = useHistory();
    const { resetPagination } = usePaginationStore();

    const isSkeletonRow = (row) => {
        return Object.values(row).some(value => React.isValidElement(value) && value.type === Skeleton);
    };

    const onRowClick = useCallback((e) => {
        if (isSkeletonRow(e.data)) return;
        resetPagination();

        const candidateId = e.data.candidate_id;
        history.push(`/dashboard/campaigns/${campaign.campaign_id}/candidates/${candidateId}`);
    }, [campaign, history]);

    const onSelectionChange = useCallback((e) => {
        const filteredRows = e.value.filter(row => !isSkeletonRow(row));
        setSelectedRows(filteredRows);
        const selectedIds = filteredRows.map(row => row.candidate_id);
        setSelectedCandidateIds(selectedIds);
    }, []);

    const onHeaderClick = useCallback((field) => {
        console.log(`Sorting by ${field}`);
    }, []);

    const headerTemplate = useCallback((options) => (
        <>
            <span onClick={() => onHeaderClick(options.field)} className='is-clickable'>
                {options.header}
            </span>
            <i className="fa-solid fa-chevron-down right-icon"></i>
        </>
    ), [onHeaderClick]);

    const choosenTestsBodyTemplate = useCallback((rowData) => {
        if (loading) return <Skeleton width="100%" />;

        let statusClass = '';

        if (!rowData.choosen_tests || rowData.choosen_tests.length === 0) {
            statusClass = 'd-inactive-tag';
        } else if (rowData.choosen_tests.length === campaign.choosen_tests.length) {
            statusClass = 'd-success-tag';
        } else {
            statusClass = 'd-pause-tag';
        }

        return (
            <span className={`${statusClass} no-wrap`}>
                {i18next.t(`userDashboard.campaigns.participants_table_tag.${!rowData.choosen_tests ? '0' :
                        rowData.choosen_tests.length === campaign.choosen_tests.length ? '1' : '2'
                    }`)}
            </span>
        );
    }, [loading, campaign]);

    const generateSkeletonData = useMemo(() => {
        return Array.from({ length: 10 }, (_, index) => ({
            candidate_id: index,
            username: <Skeleton width="100%" />,
            first_name: <Skeleton width="100%" />,
            last_name: <Skeleton width="100%" />,
            email: <Skeleton width="100%" />,
            choosen_tests: <Skeleton width="100%" />,
            created_at: <Skeleton width="100%" />
        }));
    }, []);

    const displayData = loading ? generateSkeletonData : data;

    if (!loading && !data.length && campaign) return null;

    return (
        <DataTable
            dataKey="candidate_id"
            stripedRows
            sortMode="multiple"
            value={displayData}
            selection={selectedRows}
            onSelectionChange={onSelectionChange}
            selectionMode="checkbox"
            metaKeySelection={false}
            onRowClick={onRowClick}>

            <Column key="selection" selectionMode="multiple" headerStyle={{ width: '3em' }} />

            <Column key="username" sortable field="username" header={i18next.t('userDashboard.campaigns.participants_table.0')} body={(rowData) => <strong>{rowData.username}</strong>} headerTemplate={headerTemplate} />

            <Column key="first_name" sortable field="first_name" header={i18next.t('userDashboard.campaigns.participants_table.1')} headerTemplate={headerTemplate} />

            <Column key="last_name" sortable field="last_name" header={i18next.t('userDashboard.campaigns.participants_table.2')} headerTemplate={headerTemplate} />

            <Column key="email" sortable field="email" header={i18next.t('userDashboard.campaigns.participants_table.3')} headerTemplate={headerTemplate} />

            <Column key="choosen_tests" field="choosen_tests" header={i18next.t('userDashboard.campaigns.participants_table.4')} body={choosenTestsBodyTemplate} />

            <Column key="created_at" sortable field="created_at" header={i18next.t('userDashboard.campaigns.participants_table.5')} headerTemplate={headerTemplate} />
        </DataTable>
    );
};

export default CandidatesTable;